import { useMemo, useState } from 'react'
import {
  ExpandLessOutlined as ExpandLessOutlinedIcon,
  ExpandMoreOutlined as ExpandMoreOutlinedIcon,
  Inventory2Outlined as Inventory2OutlinedIcon
} from '@mui/icons-material'
import { MOVEMENT_TYPE_OPTIONS } from '../../../../../../../constants/inventoryLabels'
import Badge from '../../components/Badge'
import DataTable from '../../components/DataTable'
import SectionCard from '../../components/SectionCard'
import { formatDate } from '../../utils/inventoryFormatters'
import { buildMovementFeed, filterMovementRows, getMovementEmptyState } from '../../utils/inventoryMovements'

const quickMovementFilters = [
  { value: '', label: 'Todos' },
  { value: 'IN_INITIAL_LOAD', label: 'Carga inicial' },
  { value: 'IN_MANUAL', label: 'Entrada' },
  { value: 'OUT_SALE', label: 'Venta' },
  { value: 'OUT_MERMA', label: 'Merma' },
  { value: 'OUT_THEFT', label: 'Robo' },
  { value: 'ADJ_POS', label: 'Ajuste +' },
  { value: 'ADJ_NEG', label: 'Ajuste -' }
]

const impactToneClassNames = {
  green: 'border-green-200 bg-green-50 text-green-700',
  red: 'border-red-200 bg-red-50 text-red-700',
  blue: 'border-blue-200 bg-blue-50 text-blue-700',
  neutral: 'border-amber-200 bg-amber-50 text-amber-700'
}

const DetailField = ({ label, value, mono = false }) => {
  if (!value) return null

  return (
    <div>
      <p className='text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500'>{label}</p>
      <p className={`mt-1 text-sm text-slate-700 ${mono ? 'break-all font-mono text-[13px]' : ''}`}>{value}</p>
    </div>
  )
}

const ImpactPill = ({ text, tone }) => (
  <span
    className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${
      impactToneClassNames[tone] || impactToneClassNames.blue
    }`}
  >
    {text}
  </span>
)

const MetaPill = ({ children }) => (
  <span className='inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600'>
    {children}
  </span>
)

const EmptyMovementsState = ({ message }) => (
  <div className='rounded-2xl border border-dashed border-slate-300 bg-slate-50/70 px-4 py-8 text-center'>
    <div className='mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400'>
      <Inventory2OutlinedIcon />
    </div>
    <p className='mt-3 text-base font-semibold text-slate-800'>{message}</p>
    <p className='mt-1 text-sm text-slate-500'>
      Cuando existan movimientos, aquí verás un historial simple con detalle expandible.
    </p>
  </div>
)

const MovementRowSummary = ({ entry, expanded, hideWarehouseLabel, onToggle }) => {
  const hasNote = Boolean(entry.notePreview)
  const hasWarehouse = !hideWarehouseLabel && entry.warehouseLabel && entry.warehouseLabel !== '-'
  const detailId = `movement-entry-${entry.id}`

  return (
    <article className='overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.05)]'>
      <div className='flex flex-col gap-3 p-4 md:flex-row md:items-start md:justify-between'>
        <div className='min-w-0 flex-1'>
          <div className='flex flex-wrap items-center gap-2'>
            <Badge text={entry.typeLabel} tone={entry.tone} />
            <span className='text-xs font-medium text-slate-500'>{formatDate(entry.dateValue)}</span>
            {entry.kind === 'group' && <MetaPill>{entry.productsCount} productos</MetaPill>}
          </div>

          <h3 className='mt-2 text-sm font-semibold leading-5 text-slate-900 md:text-base'>{entry.title}</h3>

          <div className='mt-2 flex flex-wrap items-center gap-2'>
            <ImpactPill text={entry.impactLabel} tone={entry.tone} />
            {hasWarehouse && <MetaPill>Bodega {entry.warehouseLabel}</MetaPill>}
          </div>

          {hasNote && <p className='mt-2 text-sm leading-5 text-slate-600'>Nota: {entry.notePreview}</p>}
        </div>

        <button
          type='button'
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={detailId}
          className='inline-flex items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50'
        >
          {expanded ? 'Ocultar detalle' : 'Ver detalle'}
          {expanded ? <ExpandLessOutlinedIcon fontSize='small' /> : <ExpandMoreOutlinedIcon fontSize='small' />}
        </button>
      </div>

      {expanded && (
        <div id={detailId} className='border-t border-slate-200 bg-slate-50/70 p-4'>
          {entry.kind === 'group' ? (
            <GroupedMovementDetail entry={entry} />
          ) : (
            <SingleMovementDetail entry={entry} />
          )}
        </div>
      )}
    </article>
  )
}

const SingleMovementDetail = ({ entry }) => {
  const { movement } = entry

  return (
    <div className='grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_320px]'>
      <div className='rounded-xl border border-slate-200 bg-white p-4'>
        <p className='text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500'>Resumen</p>
        <div className='mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2'>
          <DetailField label='Producto' value={movement.productLabel} />
          <DetailField label='Impacto' value={entry.impactLabel} />
          <DetailField label='Bodega' value={entry.warehouseLabel && entry.warehouseLabel !== '-' ? entry.warehouseLabel : ''} />
          <DetailField label='Fecha completa' value={formatDate(entry.dateValue)} />
        </div>

        {entry.note && (
          <div className='mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3'>
            <p className='text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500'>Nota</p>
            <p className='mt-2 text-sm leading-5 text-slate-700'>{entry.note}</p>
          </div>
        )}
      </div>

      <div className='rounded-xl border border-slate-200 bg-white p-4'>
        <p className='text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500'>Trazabilidad</p>
        <div className='mt-3 space-y-3'>
          <DetailField label='Tipo de movimiento' value={entry.typeLabel} />
          <DetailField label='Referencia técnica' value={entry.reference} mono />
          <DetailField label='SKU' value={movement.sku} mono />
        </div>
      </div>
    </div>
  )
}

const GroupedMovementDetail = ({ entry }) => (
  <div className='grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_320px]'>
    <div className='rounded-xl border border-slate-200 bg-white p-3'>
      <p className='mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500'>Productos incluidos</p>
      <DataTable
        columns={['Producto', 'Impacto', 'Nota']}
        rows={entry.movements}
        emptyText='Sin detalle'
        renderRow={movement => (
          <>
            <td className='px-4 py-2 align-top'>
              <div className='font-medium text-slate-800'>{movement.productLabel}</div>
            </td>
            <td className='px-4 py-2 align-top text-slate-700'>{movement.impactLongLabel}</td>
            <td className='px-4 py-2 align-top text-slate-600'>{movement.note || ''}</td>
          </>
        )}
      />
    </div>

    <div className='rounded-xl border border-slate-200 bg-white p-4'>
      <p className='text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500'>Trazabilidad</p>
      <div className='mt-3 space-y-3'>
        <DetailField label='Fecha completa' value={formatDate(entry.dateValue)} />
        <DetailField label='Bodega' value={entry.warehouseLabel && entry.warehouseLabel !== '-' ? entry.warehouseLabel : ''} />
        <DetailField label='Referencia técnica' value={entry.reference} mono />
        <DetailField label='Totales' value={`${entry.productsCount} productos · ${entry.impactLabel}`} />
      </div>

      {entry.note && (
        <div className='mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3'>
          <p className='text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500'>Notas relacionadas</p>
          <p className='mt-2 text-sm leading-5 text-slate-700'>{entry.note}</p>
        </div>
      )}
    </div>
  </div>
)

const MovementsSection = ({ data, state }) => {
  const [expandedRows, setExpandedRows] = useState(() => new Set())

  const filteredMovementRows = useMemo(
    () =>
      filterMovementRows(data.movements || [], {
        movementType: state.movementType,
        searchQuery: state.movementSearch
      }),
    [data.movements, state.movementSearch, state.movementType]
  )
  const movementFeed = useMemo(() => buildMovementFeed(filteredMovementRows), [filteredMovementRows])
  const warehouseContextLabel = useMemo(() => {
    const labels = Array.from(
      new Set(
        movementFeed
          .map(entry => entry.warehouseLabel)
          .filter(label => label && label !== '-')
      )
    )

    if (labels.length !== 1 || movementFeed.length <= 1) return ''
    return labels[0]
  }, [movementFeed])

  const loading = data.movementsQuery.isLoading || data.isLoadingAny
  const hasFilters = Boolean(state.movementSearch.trim() || state.movementType)
  const emptyStateMessage = getMovementEmptyState({
    hasFilters,
    hasRows: movementFeed.length > 0
  })

  const handleToggleRow = entryId => {
    setExpandedRows(previous => {
      const next = new Set(previous)

      if (next.has(entryId)) {
        next.delete(entryId)
      } else {
        next.add(entryId)
      }

      return next
    })
  }

  return (
    <SectionCard
      title='Ajustes de inventario'
      description='Revisa entradas, salidas, ajustes y cargas iniciales de forma simple.'
    >
      <div className='mb-4 grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_260px]'>
        <input
          type='text'
          value={state.movementSearch}
          onChange={event => state.setMovementSearch(event.target.value)}
          className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
          placeholder='Buscar por SKU o referencia'
        />

        <select
          value={state.movementType}
          onChange={event => state.setMovementType(event.target.value)}
          className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
        >
          <option value=''>Todos los tipos de movimiento</option>
          {MOVEMENT_TYPE_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className='mb-4 overflow-x-auto pb-1'>
        <div className='flex min-w-max items-center gap-2'>
          {quickMovementFilters.map(filter => {
            const isActive = state.movementType === filter.value

            return (
              <button
                key={filter.value || 'all'}
                type='button'
                onClick={() => state.setMovementType(filter.value)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                  isActive
                    ? 'border-accent2/40 bg-accent2/[0.12] text-slate-900 shadow-[0_6px_14px_rgba(28,178,175,0.12)]'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                {filter.label}
              </button>
            )
          })}
        </div>
      </div>

      {warehouseContextLabel && (
        <div className='mb-4'>
          <MetaPill>Contexto visible: Bodega {warehouseContextLabel}</MetaPill>
        </div>
      )}

      {loading ? (
        <div className='rounded-2xl border border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500'>
          Cargando movimientos...
        </div>
      ) : movementFeed.length === 0 ? (
        <EmptyMovementsState message={emptyStateMessage} />
      ) : (
        <div className='space-y-3'>
          {movementFeed.map(entry => (
            <MovementRowSummary
              key={entry.id}
              entry={entry}
              expanded={expandedRows.has(entry.id)}
              hideWarehouseLabel={Boolean(warehouseContextLabel)}
              onToggle={() => handleToggleRow(entry.id)}
            />
          ))}
        </div>
      )}
    </SectionCard>
  )
}

export default MovementsSection
